/**
 * AST 기반 자동 생성된 검증 함수
 * 이 파일은 scripts/generate-validators.ts에 의해 자동 생성됩니다.
 * 수동으로 수정하지 마세요.
 * 
 * 생성 시간: 2026-11-05T04:17:10.255Z
 * 소스 파일: src/data/dto/hospital-info-valid-reponse-dto
 */

import type { HospitalInfoValidResponseDto } from "@/data/dto/hospital-info-valid-reponse-dto";

/**
 * HospitalInfoValidResponseDto 타입 가드 함수
 * AST 분석을 통해 자동 생성된 최적화된 검증 함수
 */
export function isHospitalInfoValidResponseDto(input: unknown): input is HospitalInfoValidResponseDto {
  if (typeof input !== "object" || input === null || Array.isArray(input)) {
    return false;
  }
  
  const obj = input as Record<string, unknown>;
  
  return (
    typeof obj.hospitalName === "string" && obj.hospitalName.trim() !== "" &&
    typeof obj.isValide === "boolean"
  );
}

/**
 * HospitalInfoValidResponseDto 상세 검증 결과 반환 함수
 * 검증 실패 시 상세한 에러 정보를 반환합니다.
 */
export interface IValidation<T> {
  success: boolean;
  data?: T;
  errors?: Array<{ path: string; expected: string; value: unknown }>;
}

export function validateHospitalInfoValidResponseDto(input: unknown): IValidation<HospitalInfoValidResponseDto> {
  const errors: Array<{ path: string; expected: string; value: unknown }> = [];
  
  if (typeof input !== "object" || input === null || Array.isArray(input)) {
    errors.push({
      path: "",
      expected: "object",
      value: input,
    });
    return {
      success: false,
      errors,
    };
  }
  
  const obj = input as Record<string, unknown>;
  
    if (typeof obj !== "object" || obj === null || Array.isArray(obj)) {
      errors.push({ path: "", expected: "object", value: obj });
    } else {
      const objObj = obj as Record<string, unknown>;
      // hospitalName (required)
      if (objObj.hospitalName === undefined) {
        errors.push({ path: "hospitalName", expected: "string", value: undefined });
      } else {
        if (!(typeof objObj.hospitalName === "string" && objObj.hospitalName.trim() !== "")) {
          errors.push({ path: "hospitalName", expected: "string (non-empty)", value: objObj.hospitalName });
        }
      }
      // isValide (required)
      if (objObj.isValide === undefined) {
        errors.push({ path: "isValide", expected: "boolean", value: undefined });
      } else {
        if (typeof objObj.isValide !== "boolean") {
          errors.push({ path: "isValide", expected: "boolean", value: objObj.isValide });
        }
      }
    }
  
  if (errors.length === 0 && isHospitalInfoValidResponseDto(input)) {
    return {
      success: true,
      data: input,
    };
  }
  
  return {
    success: false,
    errors: errors.length > 0 ? errors : [
      {
        path: "",
        expected: "HospitalInfoValidResponseDto",
        value: input,
      },
    ],
  };
}

/**
 * HospitalInfoValidResponseDto 검증 후 에러 throw 함수
 * 검증 실패 시 상세한 에러 정보를 포함하여 에러를 throw합니다.
 */
export function assertHospitalInfoValidResponseDto(input: unknown): HospitalInfoValidResponseDto {
  const validation = validateHospitalInfoValidResponseDto(input);
  if (!validation.success) {
    const errorDetails = validation.errors?.map(err => 
      `  - ${err.path}: 예상 타입 ${err.expected}, 실제 값: ${JSON.stringify(err.value)}`
    ).join("\n") || "알 수 없는 오류";
    throw new Error(`[assertHospitalInfoValidResponseDto] :: 검증 실패: HospitalInfoValidResponseDto 타입과 일치하지 않습니다\n\n오류 상세:\n${errorDetails}`);
  }
  return validation.data!;
}
